/**
 * SurveyDashboard — Active survey overview, area progress, sync status.
 * Route: /visits/:visitId/survey
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Play, Pause, ClipboardList, CheckSquare, Navigation, Upload, AlertCircle, BookOpen, MapPinned, Building2, UserRound, Stethoscope, FolderOpen, ChevronDown, ChevronUp } from 'lucide-react'
import { getSurvey, startSurvey, pauseSurvey, resumeSurvey, type SurveySession } from '../../lib/services/survey'
import { getDocumentRequests, getPrestudyDocuments, PRESTUDY_DOC_CATEGORIES, type PrestudyRequest, type PrestudyDocument } from '../../lib/services/prestudy'
import { getAssignments, type Visit } from '../../lib/services/visits'
import { surveyStore } from '../../lib/offline/surveyStore'
import { isAuthFailure, authFailureMessage, shouldRetryRequest } from '../../lib/errors'
import { useAuth } from '../../context/AuthContext'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'

function formatVisitDateKey(value: string | null | undefined) {
  return (value || '').slice(0, 10)
}

function fallbackVisit(visitId: string | undefined, session: SurveySession | null): Visit | null {
  if (!visitId && !session) return null
  return {
    id: visitId || session?.visit_id || 'current-visit',
    facility_id: null,
    visit_type: 'unknown',
    facility_name: session?.facility_name || 'Selected facility',
    address: 'Facility address not recorded',
    city: null,
    state: null,
    zip_code: null,
    administrator_name: null,
    don_name: null,
    specialties: [],
    visit_scope: 'Survey scope',
    status: session?.status === 'active' || session?.status === 'paused' ? 'in_progress' : 'scheduled',
    surveyor_name: 'Assigned Surveyor',
    visit_date: session?.started_at || new Date().toISOString(),
  }
}

export function SurveyDashboard() {
  const { visitId } = useParams<{ visitId: string }>()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()

  const { data: survey, isLoading, error } = useQuery({
    queryKey: ['survey', visitId],
    queryFn: () => getSurvey(visitId!),
    enabled: !!visitId,
    retry: shouldRetryRequest,
  })

  // Offline session fallback
  const [offlineSession, setOfflineSession] = useState<SurveySession | null>(null)
  useEffect(() => {
    if (!survey && visitId) {
      surveyStore.getSessionByVisit(visitId).then((s) => setOfflineSession((current) => s ?? current ?? null))
    }
  }, [survey, visitId])

  const session = survey ?? offlineSession

  const { data: assignedVisits = [] } = useQuery({
    queryKey: ['visits', 'assignments'],
    queryFn: getAssignments,
    retry: shouldRetryRequest,
  })

  const selectedVisit = useMemo(() => {
    return assignedVisits.find((visit) => visit.id === visitId) ?? fallbackVisit(visitId, session)
  }, [assignedVisits, session, visitId])

  const dailyRouteVisits = useMemo(() => {
    if (!selectedVisit) return []
    const selectedDate = formatVisitDateKey(selectedVisit.visit_date)
    const sameDayVisits = assignedVisits.filter((visit) => formatVisitDateKey(visit.visit_date) === selectedDate)
    return sameDayVisits.length > 0 ? sameDayVisits : [selectedVisit]
  }, [assignedVisits, selectedVisit])

  const showRouteMap = dailyRouteVisits.length > 1
  const [routeMapExpanded, setRouteMapExpanded] = useState(false)

  async function promoteSession(data: SurveySession) {
    setOfflineSession(data)
    queryClient.setQueryData(['survey', visitId], data)

    try {
      await surveyStore.saveSession(data)
    } catch (error) {
      console.error('[SurveyDashboard] Failed to persist survey session offline', error)
    }

    await queryClient.invalidateQueries({ queryKey: ['survey', visitId] })
  }

  const pauseMutation = useMutation({
    mutationFn: () => pauseSurvey(visitId!, session!.survey_id),
    onSuccess: async (data) => promoteSession(data),
  })

  const resumeMutation = useMutation({
    mutationFn: () => resumeSurvey(visitId!, session!.survey_id),
    onSuccess: async (data) => promoteSession(data),
  })

  // Prestudy document requests
  const [docRequests, setDocRequests] = useState<PrestudyRequest[]>([])
  const [prestudyDocs, setPrestudyDocs] = useState<PrestudyDocument[]>([])
  useEffect(() => {
    if (!visitId) return
    getDocumentRequests(visitId).then(setDocRequests).catch(() => {})
    getPrestudyDocuments(visitId).then(setPrestudyDocs).catch(() => {})
  }, [visitId])

  // Framework selection state — must be before any conditional returns
  const [framework, setFramework] = useState<'CMS' | 'TJC' | 'AAAHC'>('CMS')
  const startMutation = useMutation({
    mutationFn: () => startSurvey(visitId!, framework),
    onSuccess: async (data) => promoteSession(data),
  })
  const terminalSurveyRoute =
    selectedVisit?.status === 'in_review'
    || selectedVisit?.status === 'released'
    || selectedVisit?.status === 'archived'
    || session?.status === 'completed'

  useEffect(() => {
    if (!isLoading && visitId && terminalSurveyRoute) {
      navigate(`/visits/${visitId}/survey/report`, { replace: true })
    }
  }, [isLoading, navigate, terminalSurveyRoute, visitId])

  if (isLoading) {
    return (
      <FieldAppShell eyebrow="Survey" title="Survey Dashboard" showBackControl={false}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]" />
        </div>
      </FieldAppShell>
    )
  }

  if (error) {
    const isAuth = isAuthFailure(error)
    return (
      <FieldAppShell eyebrow="Survey" title="Survey Dashboard" showBackControl={false}>
        <div className="app-surface p-6 text-center space-y-3">
          <p className="text-sm text-red-600">{isAuth ? authFailureMessage(error) : 'Failed to load survey'}</p>
          {isAuth && (
            <button type="button" onClick={() => signOut()} className="btn-ghost text-sm">
              Sign out
            </button>
          )}
        </div>
      </FieldAppShell>
    )
  }

  // No active survey — show start button with framework selector
  if (!session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Survey Route" showBackControl={false}>
      <div className="space-y-6 px-4 py-6">
        <h2 className="section-title">Survey Route</h2>
        <div className="app-surface p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-[var(--brand-primary)]" />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Assigned facility</p>
              <p className="text-sm font-bold text-slate-800">{selectedVisit?.facility_name ?? 'Selected facility'}</p>
            </div>
          </div>

          {dailyRouteVisits.length > 0 && (
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Facility selection</span>
              <select
                value={visitId ?? ''}
                onChange={(event) => navigate(`/visits/${event.target.value}/survey`)}
                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                {dailyRouteVisits.map((visit) => (
                  <option key={visit.id} value={visit.id}>
                    {visit.facility_name} - {visit.visit_scope ?? 'Survey scope'}
                  </option>
                ))}
              </select>
            </label>
          )}

          {showRouteMap && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Today&apos;s route</p>
                <button
                  type="button"
                  onClick={() => setRouteMapExpanded((current) => !current)}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-600 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                >
                  {routeMapExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {routeMapExpanded ? 'Collapse map' : 'Expand map'}
                </button>
              </div>
              <div
                aria-label="Map view of today's survey route"
                className={`relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50 ${routeMapExpanded ? 'min-h-44' : 'min-h-28'}`}
              >
                <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-slate-300" />
                {dailyRouteVisits.slice(0, 4).map((visit, index) => {
                  const positions = ['left-5 top-5', 'right-6 top-9', 'left-1/2 bottom-5 -translate-x-1/2', 'right-14 bottom-8']
                  return (
                    <div
                      key={visit.id}
                      className={`absolute ${positions[index] ?? positions[0]} max-w-40 rounded-lg border border-[var(--brand-primary)] bg-white px-2 py-1 text-[11px] font-bold text-slate-800 shadow-sm`}
                    >
                      <MapPinned size={12} className="inline mr-1 text-[var(--brand-primary)]" />
                      {index + 1}. {visit.facility_name}
                      {routeMapExpanded && (visit.specialties ?? []).length > 0 && (
                        <p className="mt-0.5 text-[10px] font-medium text-slate-500">{(visit.specialties ?? []).join(', ')}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {selectedVisit && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Selected facility detail</p>
              <div className="grid gap-2 text-xs text-slate-700">
                <div className="flex items-start gap-2">
                  <UserRound size={14} className="mt-0.5 text-slate-400" />
                  <span><b>Administrator</b><br />{selectedVisit.administrator_name ?? 'Not recorded'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <UserRound size={14} className="mt-0.5 text-slate-400" />
                  <span><b>DON</b><br />{selectedVisit.don_name ?? 'Not recorded'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPinned size={14} className="mt-0.5 text-slate-400" />
                  <span><b>Address</b><br />{selectedVisit.address}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Stethoscope size={14} className="mt-0.5 text-slate-400" />
                  <span><b>Specialties</b><br />{(selectedVisit.specialties ?? []).length > 0 ? (selectedVisit.specialties ?? []).join(', ') : 'Not recorded'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <ClipboardList size={14} className="mt-0.5 text-slate-400" />
                  <span><b>Visit scope</b><br />{selectedVisit.visit_scope ?? 'Survey scope'}</span>
                </div>
              </div>
              <Link
                to={`/visits/${selectedVisit.id}/survey/documents`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
              >
                <FolderOpen size={14} />
                View uploaded facility documents
              </Link>
            </div>
          )}

          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Accreditation Framework</span>
            <div className="flex gap-2">
              {(['CMS', 'TJC', 'AAAHC'] as const).map((fw) => (
                <button
                  key={fw}
                  type="button"
                  onClick={() => setFramework(fw)}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-colors ${
                    framework === fw
                      ? 'bg-[var(--brand-primary)] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {fw}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">
              {framework === 'CMS' && 'CMS Conditions for Coverage only (42 CFR 416)'}
              {framework === 'TJC' && 'CMS CfCs + The Joint Commission standards cross-references'}
              {framework === 'AAAHC' && 'CMS CfCs + AAAHC accreditation standards cross-references'}
            </p>
          </div>

          <button
            type="button"
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
            className="w-full rounded-lg bg-amber-400 px-4 py-3 text-sm font-black text-slate-950 transition-colors hover:bg-amber-300 disabled:opacity-50"
          >
            <Play size={16} className="inline mr-2" />
            {startMutation.isPending ? 'Starting...' : 'Start survey'}
          </button>
        </div>
      </div>
      </FieldAppShell>
    )
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Survey Dashboard" showBackControl={false} rightSlot={<SyncStatus />}>
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <h2 className="section-title">Survey Dashboard</h2>
        <SyncStatus />
      </div>

      <div className="app-surface p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-[var(--brand-primary)]" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Assigned facility</p>
            <p className="text-sm font-bold text-slate-800">{(selectedVisit?.facility_name ?? session.facility_name) || 'Facility'}</p>
          </div>
        </div>

        {dailyRouteVisits.length > 0 && (
          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Facility selection</span>
            <select
              value={visitId ?? ''}
              onChange={(event) => navigate(`/visits/${event.target.value}/survey`)}
              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {dailyRouteVisits.map((visit) => (
                <option key={visit.id} value={visit.id}>
                  {visit.facility_name} - {visit.visit_scope ?? 'Survey scope'}
                </option>
              ))}
            </select>
          </label>
        )}

        {showRouteMap && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-bold uppercase tracking-wider text-slate-500">Today&apos;s route</span>
              <button
                type="button"
                onClick={() => setRouteMapExpanded((current) => !current)}
                className="font-bold text-[var(--brand-primary)]"
              >
                {routeMapExpanded ? 'Collapse map' : 'Expand map'}
              </button>
            </div>
            <div className={`relative overflow-hidden rounded-lg bg-white ${routeMapExpanded ? 'min-h-40' : 'min-h-24'}`}>
              <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-slate-300" />
              {dailyRouteVisits.slice(0, 4).map((visit, index) => {
                const positions = ['left-4 top-4', 'right-5 top-7', 'left-1/2 bottom-4 -translate-x-1/2', 'right-12 bottom-7']
                return (
                  <div key={visit.id} className={`absolute ${positions[index] ?? positions[0]} max-w-36 rounded-lg border border-[var(--brand-primary)] bg-white px-2 py-1 font-bold shadow-sm`}>
                    {index + 1}. {visit.facility_name}
                    {routeMapExpanded && (visit.specialties ?? []).length > 0 && (
                      <p className="mt-0.5 font-medium text-slate-500">{(visit.specialties ?? []).join(', ')}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {selectedVisit && (
          <div className="grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <span><b>Administrator</b>: {selectedVisit.administrator_name ?? 'Not recorded'}</span>
            <span><b>DON</b>: {selectedVisit.don_name ?? 'Not recorded'}</span>
            <span><b>Address</b>: {selectedVisit.address}</span>
            <span><b>Specialties</b>: {(selectedVisit.specialties ?? []).length > 0 ? (selectedVisit.specialties ?? []).join(', ') : 'Not recorded'}</span>
            <span><b>Visit scope</b>: {selectedVisit.visit_scope ?? 'Survey scope'}</span>
            <Link
              to={`/visits/${selectedVisit.id}/survey/documents`}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
            >
              <FolderOpen size={14} />
              View uploaded facility documents
            </Link>
          </div>
        )}
      </div>

      {/* Session info */}
      <div className="app-surface p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">{session.facility_name || 'Facility'}</span>
          <span className={session.status === 'active' ? 'badge-in-progress' : 'badge-scheduled'}>
            {session.status}
          </span>
        </div>
        <p className="text-xs text-slate-500">
          Started {new Date(session.started_at).toLocaleString()}
        </p>
        {session.status === 'active' && (
          <button
            type="button"
            onClick={() => pauseMutation.mutate()}
            disabled={pauseMutation.isPending}
            className="btn-ghost text-xs py-1.5 px-3"
          >
            <Pause size={12} className="inline mr-1" /> Pause
          </button>
        )}
        {session.status === 'paused' && (
          <button
            type="button"
            onClick={() => resumeMutation.mutate()}
            disabled={resumeMutation.isPending}
            className="btn-primary text-xs py-1.5 px-3"
          >
            <Play size={12} className="inline mr-1" /> Resume
          </button>
        )}
      </div>

      {/* Guided walkthrough — primary survey action */}
      <Link
        to="walkthrough"
        className="app-surface p-5 flex items-center gap-4 border-2 border-[var(--brand-primary)] hover:bg-blue-50 transition-colors"
      >
        <Navigation size={32} className="text-[var(--brand-primary)] flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm font-bold text-[var(--brand-primary)]">Begin Guided Survey</span>
          <p className="text-xs text-slate-500 mt-0.5">
            Step-by-step walkthrough of every area with CMS/TJC/AAAHC guidance on what to inspect.
            Photos and findings auto-tagged by area and domain.
          </p>
        </div>
      </Link>

      {/* Draft Report */}
      <Link
        to="report"
        className="app-surface p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
      >
        <BookOpen size={28} className="text-[var(--brand-primary)] flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm font-bold text-slate-800">Draft Report</span>
          <p className="text-xs text-slate-500 mt-0.5">
            View and edit the survey findings report, SAFER matrix, and corrective action plan.
          </p>
        </div>
      </Link>

      {/* Quick-access grid removed per SME feedback — the sub-pages are
          only reachable mid-walkthrough, so the dashboard shortcuts led to
          empty/broken states. Access flows through "Begin Guided Survey"
          instead. (SME feedback 2026-04-15 issue #1298.) */}

      {/* Pre-survey document requests */}
      {docRequests.length > 0 && (() => {
        const allRequestedTypes = [...new Set(docRequests.flatMap((r) => r.requested_docs))]
        const uploadedTypes = new Set<string>(prestudyDocs.map((d) => d.doc_type))
        const pendingTypes = allRequestedTypes.filter((t) => !uploadedTypes.has(t))

        return (
          <div className="app-surface p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Pre-Survey Documents
              </span>
              <span className="text-[10px] text-slate-400">
                {prestudyDocs.length}/{allRequestedTypes.length} uploaded
              </span>
            </div>

            {pendingTypes.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                <AlertCircle size={12} className="inline mr-1" />
                {pendingTypes.length} document type{pendingTypes.length > 1 ? 's' : ''} still needed
              </div>
            )}

            <div className="space-y-1.5">
              {allRequestedTypes.map((docType) => {
                const cat = PRESTUDY_DOC_CATEGORIES.find((c) => c.type === docType)
                const uploaded = uploadedTypes.has(docType)
                return (
                  <div key={docType} className="flex items-center gap-2 text-xs">
                    {uploaded ? (
                      <CheckSquare size={14} className="text-emerald-500" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded border-2 border-slate-300" />
                    )}
                    <span className={uploaded ? 'text-emerald-700' : 'text-slate-700'}>
                      {cat?.label ?? docType}
                    </span>
                  </div>
                )
              })}
            </div>

            {pendingTypes.length > 0 && (
              <Link
                to={`/visits/${visitId}/prestudy/upload`}
                className="btn-primary w-full text-center text-sm py-2 inline-flex items-center justify-center gap-2"
              >
                <Upload size={14} />
                Upload Documents
              </Link>
            )}
          </div>
        )
      })()}

      {/* Areas visited */}
      {session.areas_visited.length > 0 && (
        <div className="app-surface p-4 space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Areas Visited</span>
          <div className="flex flex-wrap gap-1.5">
            {session.areas_visited.map((area) => (
              <span key={area} className="brand-chip">{area}</span>
            ))}
          </div>
        </div>
      )}
    </div>
    </FieldAppShell>
  )
}
